import { CSSProperties, Fragment, useEffect, useState } from "react";
import useCampaignDetails, { CampaignExistsState } from "Hooks/useCampaignDetails";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import { clsx, formatNumberToDisplayType, getCampaignTypeInfo, makeKey, styx } from "modules/Utils";
import useScreen from "Hooks/useScreen";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import TagSymphony from "elements/TagSymphony";
import TabSymphony from "elements/TabSymphony";
import CardSymphony from "elements/CardSymphony";
import Stat, { StatItem } from "elements/Stat";
import Sty from "modules/Sty";
import AreaChartSymphony, { AreaChartSymphonyReferenceLine } from "elements/AreaChartSymphony";
import { CampaignCreationErrorCodes, MediaType } from "modules/Types";
import StatProgress from "elements/StatProgress";
import Loader from "elements/Loader";
import DemographicsStats from "./MarketingPage/CampaignDetails/DemographicsStats";
import CampaignDetailsHeader from "elements/CampaignDetailsHeader";
import useBrand from "Hooks/useBrand";
import AdPreview from "elements/AdPreview";
import { AssetPlatformType } from "components/CampaignViews/AdCreativeSelectorCampaignView";
import Banner from "elements/Banner";
import { AdPlatform, CAMPAIGN_STATUS_INFO, CampaignStatus, PLAYBOOK_TYPE_INFO, PlaybookType, PlanType } from "modules/Const";
import StreamingLinksBreakdown from "./MarketingPage/CampaignDetails/StreamingLinksBreakdown";
import Colors from "modules/Colors";
import { DailyStat, unlockAdBoxAnalytics_api } from "api/campaignApi";
import dayjs from "dayjs";
import { Caption, CreativeSelectorPost } from "Hooks/useCampaignBuilder";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import Intercom from "helpers/Intercom";
import { showNewMessageIntercom } from "components/Navbar/Navbar";
import { toastError, toastSuccess } from "modules/Toast";
import MaestroBanner from "./MarketingPage/MaestroBanner";
import BlurredGraph from "elements/BlurredGraph";
import useFacebookConnections from "Hooks/useFacebookConnections";
import Icon from "elements/Icon";
import ModalSymphony from "elements/ModalSymphony";
import AdCreativeSelectorCampaign from "components/CampaignViews/AdCreativeSelectorCampaignView";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { useFeature } from 'Hooks/useFeature';
import { FeatureKeys } from 'features/definitions';

const CAMPAGIN_DETAILS_TABS: TabItem[] = [
    {
        id: 'impact',
        title: 'Overall',
    },
    {
        id: 'demographics',
        title: 'Demographics',
    },

    // {
    //     id: 'audiences',
    //     title: 'Audiences',
    // },
    {
        id: 'assets',
        title: 'Assets',
    },
    {
        id: 'daily_stats',
        title: 'Daily Stats',
    },
    {
        id: 'setup_details',
        title: 'Setup Details',
    },
    {
        id: 'markets',
        title: 'Markets',
    },
]

const getTabsForPlaybook = (playbook: PlaybookType) => {
    if (playbook === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
        // TODO: Should we get rid of this function?
        return [
            ...CAMPAGIN_DETAILS_TABS,
        ]
    } else {
        return CAMPAGIN_DETAILS_TABS;
    }
}

const getDailyStatsInfo = (playbook: PlaybookType) => {
    return [
        {
            key: 'totalSpend',
            title: 'Spend over time',
            description: 'Total monetary amount spent on the campaign across all platforms and ad placements',
        },
        {
            key: 'totalClicks',
            title: 'Total Clicks',
            description: 'Number of times users clicked on any part of your ad, including links, call-to-action buttons, and media elements'
        },
        {
            key: 'totalReach',
            title: 'Total Reach',
            description: 'Number of unique users who saw your ad at least once across all platforms and placements'
        },
        {
            key: 'totalImpressions',
            title: 'Total Impressions',
            description: 'Total number of times your ad was displayed to users, including multiple views by the same user'
        },
        // Tiktok doesn't deliver this data
        ...(playbook !== PlaybookType.TIKTOK_BOOST_ENGAGEMENT ? [
            {
                key: 'inlineClicks',
                title: 'Inline Clicks',
                description: 'Number of clicks specifically on links within your ad content, excluding other clickable elements'
            },
            {
                key: 'cpic',
                title: 'Cost Per Inline Click',
                description: 'Average cost spent for each inline link click, calculated by dividing total spend by number of inline clicks'
            },
        ] : []),
        {
            key: 'cpc',
            title: 'Cost Per Click',
            description: 'Average cost spent for each click on any part of your ad, calculated by dividing total spend by total clicks'
        },
        {
            key: 'costPerConversion',
            title: 'Cost Per Conversion',
            description: 'Average cost required to achieve one conversion action (such as a sign-up, purchase, or other defined goal)'
        },
        {
            key: 'conversions',
            title: 'Conversions',
            description: 'Total number of times users completed your defined conversion actions after interacting with your ad'
        }
    ]
}

// Add this before the component definition
// Helper function to check if a campaign can have assets uploaded
const canUploadAssets = (status: string) => {
    const invalidStates = [
        CampaignStatus.DENIED,
        CampaignStatus.DRAFT,
        CampaignStatus.ERROR,
        CampaignStatus.TO_RETRY,
        CampaignStatus.FINISHED
    ];
    return !invalidStates.includes(status as CampaignStatus);
};

interface CampaignDetailsV2Props {
    className: string
    isPublic?: boolean
}

// NOTE: component()
const CampaignDetailsV2 = ({
    className,
    isPublic = false
}: CampaignDetailsV2Props) => {
    const history = useHistory();
    const location = useLocation();
    const {
        mStyle,
        mobileView,
    } = useScreen();

    const { currentBrand, isProBrand } = useBrand();
    const { isAvailable: hasAdvancedAnalytics, minRequiredPlan, availablePlans } = useFeature(FeatureKeys.ANALYTICS_ADVANCED);
    const {
        campaignId,
        campaignAnalyticsData,
        marketStatsData,
        impactStatsData,
        campaign,
        isLoadingImpactData,
        isLoadingCampaignAnalytics,
        isRetryingCampaign,
        resumeCampaign,
        getCampaignAnalytics,
        getMarketStatsData,
        getImpactData,
        resetCampaignDetails,
        adminRetryCampaign,
        setShowEditBudgetModal,
        isPausingResumingCampaign,
        setShowEditEndDateModal,
        pauseResumeAsset,
        uploadNewAssets,
    } = useCampaignDetails() as CampaignExistsState;

    const { loggedInFacebookUser, initializeFB } = useFacebookConnections();

    const {
        campaign_metadata,
        status: campaignStatus,
    } = campaign || {};

    const {
        error: mainError,
        v2_campaign_input,
        playbook,
        friendlyName: nameSetByUser,
    } = campaign_metadata;

    const { usesExpressSetup } = v2_campaign_input || {};

    let mainTabs: TabItem[] = []
    if (playbook) {
        mainTabs = getTabsForPlaybook(playbook as PlaybookType);
    }

    /** Initialize selected tab from URL query param or default to first tab */
    const initializeSelectedTab = () => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get('tab');

        // If mainTabs is empty, return a default tab to prevent undefined errors
        if (mainTabs.length === 0) {
            return CAMPAGIN_DETAILS_TABS[0]; // Use the first tab from the default tabs
        }

        if (tabFromUrl) {
            const matchingTab = mainTabs.find(tab => tab.id === tabFromUrl);
            return matchingTab || mainTabs[0];
        }
        return mainTabs[0];
    }

    const { currency } = currentBrand || {};

    const {
        marketStats,
    } = marketStatsData || {};
    const {
        budget,
        campaignLength,
        creativePerformanceStats,
        audienceStats
    } = campaignAnalyticsData || {};

    const {
        lengthConfig,
        conversionsEnabled,
        conversionsEventName,
        campaignName,
        campaignImage
    } = v2_campaign_input || {};

    const { startDate, endDate, finalStartDate, finalEndDate, daysExtended } = lengthConfig || {};

    let campaignStartDate = startDate;
    let campaignEndDate = endDate;

    if (finalStartDate) {
        campaignStartDate = finalStartDate;
    }

    if (finalEndDate) {
        campaignEndDate = finalEndDate;
    }

    const adPlatform = PLAYBOOK_TYPE_INFO[playbook as PlaybookType].adPlatform;

    const [showTotalOrDailyGraph, setShowTotalOrDailyGraph] = useState<'total' | 'daily'>('total');
    const [selectedCampaignDetailsTab, setSelectedCampaignDetailsTab] = useState<TabItem>(initializeSelectedTab());
    const [selectedMarket, setSelectedMarket] = useState<any>(null);
    const [isPausingAsset, setIsPausingAsset] = useState<string | null>(null);
    const [showUploadAssetsModal, setShowUploadAssetsModal] = useState<boolean>(false);
    const [isUploadingAssets, setIsUploadingAssets] = useState<boolean>(false);
    const [showInvalidStatusModal, setShowInvalidStatusModal] = useState(false);
    const [assetSortMetric, setAssetSortMetric] = useState<string>('conversions');
    // State for AI thoughts dropdown
    const [isAiThoughtsExpanded, setIsAiThoughtsExpanded] = useState(false);

    const {
        getAllSelectedPosts,
        resetSelectedPosts
    } = useCampaignBuilder();

    // Check if this is an adbox campaign and whether analytics are unlocked
    const isAdBoxCampaign = campaign?.campaign_metadata?.adbox_checkout === true;
    const isAdBoxAnalyticsUnlocked = campaign?.campaign_metadata?.adbox_analytics_unlocked === true;
    const shouldShowAdBoxUnlock = isAdBoxCampaign && !isAdBoxAnalyticsUnlocked && !hasAdvancedAnalytics;

    // State for tracking unlock process
    const [isUnlockingAnalytics, setIsUnlockingAnalytics] = useState<boolean>(false);

    // Handler for unlocking adbox analytics
    const handleUnlockAdBoxAnalytics = async () => {
        if (isUnlockingAnalytics) return;

        try {
            setIsUnlockingAnalytics(true);
            const response = await unlockAdBoxAnalytics_api({
                campaignId: campaignId,
                source: 'campaign_details_blurred_graph'
            });

            if (response.error || !response.url) {
                toastError('Unable to process unlock request. Please try again.');
                return;
            }

            // Redirect to checkout
            window.location.href = response.url;
        } catch (error) {
            console.error('Error unlocking analytics:', error);
            toastError('An error occurred. Please try again.');
        } finally {
            setIsUnlockingAnalytics(false);
        }
    };

    // Wrapper component for handling adbox-specific blurred graphs
    const AdBoxBlurredGraph = ({
        className,
        title,
        graph,
        buttonText,
        upgradePlan
    }: {
        className: string;
        title: string;
        graph?: string | null | 'topCountries' | 'topDemographics';
        buttonText?: string;
        upgradePlan?: PlanType;
    }) => {
        if (shouldShowAdBoxUnlock) {
            return (
                <BlurredGraph
                    className={className}
                    title={title}
                    graph={graph}
                    buttonText={isUnlockingAnalytics ? 'Processing...' : 'Unlock for $20'}
                    isButton={!isUnlockingAnalytics}
                    onClick={handleUnlockAdBoxAnalytics}
                />
            );
        }

        // For regular non-pro users, show normal upgrade prompt
        return (
            <BlurredGraph
                className={className}
                title={title}
                graph={graph}
                buttonText={buttonText || "Upgrade to Growth"}
                upgradePlan={upgradePlan || (minRequiredPlan || PlanType.GROWTH)}
            />
        );
    };

    const getCampaignDetails = async () => {
        try {
            await getCampaignAnalytics(campaignId);
            if (hasAdvancedAnalytics || isAdBoxAnalyticsUnlocked) {
                await getMarketStatsData(campaignId);
                await getImpactData(campaignId);
            }
        } catch (error: any) {
            // For errors, show a generic error message
            toastError('Failed to load campaign details. Please try again later.');
        }
    }

    useEffect(() => {
        getCampaignDetails();
        return () => {
            resetCampaignDetails();
        }
    }, []);

    useEffect(() => {
        // NOTE: init selected market
        if (marketStatsData?.marketStats?.length > 0 && !selectedMarket) {
            setSelectedMarket(campaignAnalyticsData?.markets?.[0]);
        }
    }, [marketStatsData]);

    /** Update URL when tab changes and update selected tab state */
    const handleTabChange = (tab: TabItem) => {
        const params = new URLSearchParams(location.search);
        params.set('tab', tab.id);
        history.push({ search: params.toString() });
        setSelectedCampaignDetailsTab(tab);
    }

    const getStatIcon = (name: string) => {
        switch (name) {
            case 'Clicks':
                return 'click';
            case 'Post views':
                return 'view';
            case 'Comments':
                return 'comments';
            case 'Email Signups':
                return 'metaEmailSignupsV2';
            default:
                // for conversions
                if (name?.includes('Clicks to')) {
                    return 'boost';
                }
                return 'click';
        }
    }

    const getAdCreativeTitle = () => {
        switch (adPlatform) {
            case AdPlatform.TIKTOK:
                return 'Best Performing Ad';
            case AdPlatform.META:
            default:
                return 'Creative Performance';
        }
    }

    const getAdCreativeSubtitle = () => {
        switch (adPlatform) {
            case AdPlatform.TIKTOK:
                return null;
            case AdPlatform.META:
            default:
                return 'by clicks';
        }
    }

    const handleRetryCampaign = async () => {
        try {
            /**
             *
             * @interface FacebookErrorType
             * Defines the structure for Facebook-specific errors
             * 
             * export interface FacebookErrorType {
             *     id: string;
             *     title: string;
             *     description: string;
             *     ctaText?: string;
             *     ctaLink?: string;
             *     // determines whether a user can retry campaign creation
             *     // this is mostly used before a campaign goes live
             *     canRetry?: boolean;
             *     showToUsers?: boolean;
             *     // determines whether a campaign can be unpaused after an error occurs
             *     unpauseOnRetry?: boolean;
             *     newStatus?: typeof CAMPAIGN_STATUS[keyof typeof CAMPAIGN_STATUS];
             * }
             */
            // if the Facebook Error, includes the value 'unpauseOnRetry', instead of
            // retrying campaign creation, we should instead just unpause the campaign
            let response: any = null;
            if (mainError?.unpauseOnRetry) {
                response = await resumeCampaign(campaignId, true);
            } else {
                response = await adminRetryCampaign(campaignId);
            }

            let stillHasIssues = ['TO_RETRY', 'WITH_ISSUES'].includes(response?.status);
            if (stillHasIssues) {
                throw new Error('Campaign still has issues');
            } else {
                toastSuccess('Your issue has been resolved and your campaign is now being retried!');
            }
        } catch (e) {
            toastError('It looks like there are still some issues with your campaign. Please try contacting support for more help.');
        }
    }


    const getSortedAssetPerformanceAnalytics = () => {
        const { assetPerformanceAnalytics } = campaignAnalyticsData || {};
        if (!assetPerformanceAnalytics) {
            const asset = v2_campaign_input?.assets?.[0];
            if (asset) {
                return [asset];
            }

            return [];
        }

        // Merge asset data with performance analytics
        const mergedAssets = assetPerformanceAnalytics.map(perfAsset => {
            // Find matching asset by ID
            const matchingAsset = v2_campaign_input?.assets?.find((asset: any) => asset.id === perfAsset.assetId);

            // If found, merge relevant properties (specifically paused)
            if (matchingAsset) {
                return {
                    ...perfAsset,
                    creativeId: matchingAsset.creativeId,
                    paused: matchingAsset.paused || false
                };
            }

            return perfAsset;
        });

        // First, sort by selected performance metric
        const sorted = [...mergedAssets].sort((a, b) => {
            switch (assetSortMetric) {
                case 'conversions':
                    if (a.stats.conversions !== undefined && b.stats.conversions !== undefined) {
                        return b.stats.conversions - a.stats.conversions;
                    }
                    return b.stats.clicks - a.stats.clicks;
                case 'clicks':
                    return b.stats.clicks - a.stats.clicks;
                case 'spend':
                    return b.stats.spend - a.stats.spend;
                case 'cpc':
                    // Lower CPC is better, so sort ascending
                    if (a.stats.cpc !== undefined && b.stats.cpc !== undefined) {
                        return a.stats.cpc - b.stats.cpc;
                    }
                    return b.stats.clicks - a.stats.clicks;
                case 'reach':
                    return b.stats.reach - a.stats.reach;
                case 'impressions':
                    return b.stats.impressions - a.stats.impressions;
                default:
                    // Default fallback to conversions/clicks
                    if (a.stats.conversions !== undefined && b.stats.conversions !== undefined) {
                        return b.stats.conversions - a.stats.conversions;
                    }
                    return b.stats.clicks - a.stats.clicks;
            }
        });

        // Then move paused assets to the bottom
        return sorted.sort((a, b) => {
            const aPaused = 'paused' in a ? a.paused : false;
            const bPaused = 'paused' in b ? b.paused : false;
            if (aPaused === bPaused) return 0;
            return aPaused ? 1 : -1;
        });
    }

    const renderCampaignDetailsHeader = () => {
        let headerName = nameSetByUser || campaignName || '';
        if (!headerName) {
            headerName = getCampaignTypeInfo(playbook).title;
        }

        let headerImage = campaignImage;
        let headerIcon = null;
        if (!headerImage) {
            headerImage = null
            headerIcon = getCampaignTypeInfo(playbook).icon;
        }

        return (
            <CampaignDetailsHeader
                className="campaignDetailsHeaderFromCampaignDetailsV2"
                image={headerImage}
                icon={headerIcon}
                campaignName={headerName}
                playbookType={playbook}
                campaignStatus={campaignStatus || ''}
                onBack={() => {
                    history.push('/marketing')
                }}
                isPublic={isPublic}
            />
        )
    }

    const renderTopLevelPrimaryStats = () => {
        if (!campaignAnalyticsData) {
            return null
        }

        const { topLevelPerformanceStats } = campaignAnalyticsData;
        const { primaryStats = [] } = topLevelPerformanceStats || {};

        return (
            <div
                className="topLevelPrimaryStats"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    backgroundColor: isPublic ? Colors.white : 'transparent'
                }}
            >
                {primaryStats.map((stat: any) => {
                    const { name, value, stats, subtitle } = stat;

                    const localStats = stats.map((stat: StatItem) => {
                        return {
                            title: stat.title,
                            value: stat.value,
                            displayType: stat.displayType ?? "number"
                        }
                    })

                    return (
                        <Stat
                            key={makeKey(name, stat.id)}
                            icon={getStatIcon(name)}
                            className="topLevelPrimaryStat"
                            title={name}
                            subtitle={subtitle}
                            stats={localStats}
                            isHorizontal
                        />
                    )
                })}
            </div>
        )
    }

    const renderTopLevelSecondaryStats = () => {
        if (!campaignAnalyticsData) {
            return null;
        }
        const { topLevelPerformanceStats } = campaignAnalyticsData;
        const { secondaryStats = [] } = topLevelPerformanceStats || {};
        return (
            <div
                className="topLevelSecondaryStats"
                style={{
                    display: 'grid',
                    gridTemplateColumns: mobileView ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: 8,
                }}
            >
                {secondaryStats.map((stat: StatItem, index: number) => {
                    const { title, value, displayType } = stat;
                    const localStat = {
                        title: title,
                        value: value,
                        displayType: displayType ?? "number"
                    } as StatItem;
                    return (
                        <Stat
                            key={makeKey(title, index)}
                            icon={getStatIcon(title as string)}
                            className="topLevelSecondaryStat"
                            stats={[localStat]}
                        />
                    )
                })}
            </div>
        )
    }

    const renderImpactTabGraphs = () => {
        if ([PlaybookType.TIKTOK_BOOST_ENGAGEMENT, PlaybookType.TIKTOK_MUSIC_STREAMS, PlaybookType.TIKTOK_SOUND, PlaybookType.TIKTOK_EXTERNAL_TRAFFIC].indexOf(playbook as PlaybookType) > 0) {
            return null;
        }

        // NOTE: don't show impact tab graphs for boost instagram engagement and increase link clicks
        if ([PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT, PlaybookType.INCREASE_LINK_CLICKS].indexOf(playbook as PlaybookType) > 0) {
            return null;
        }

        let hasCampaignStartedAndSpent = false;
        if ([CampaignStatus.ACTIVE, CampaignStatus.FINISHED, CampaignStatus.PAUSED].includes(campaignStatus)
            && (campaignAnalyticsData?.budget?.spent ?? 0) > 0) {
            hasCampaignStartedAndSpent = true;
        }

        let referenceLines: AreaChartSymphonyReferenceLine[] = []
        if (hasCampaignStartedAndSpent && campaignStartDate) {
            referenceLines.push({
                title: 'Campaign Start',
                date: campaignStartDate,
                color: '#42c221'
            })
        }

        let impactContent: any = null;

        if (!hasAdvancedAnalytics && !isAdBoxAnalyticsUnlocked) {
            impactContent = (
                <AdBoxBlurredGraph
                    className="impactTabGraphsSelectBlurredGraph"
                    title="Track your campaign's impact over time"
                    graph={null}
                    buttonText="Upgrade to Growth"
                    upgradePlan={minRequiredPlan || PlanType.GROWTH}
                />
            )
        } else if (isLoadingImpactData) {
            impactContent = (
                <Loader
                    className="impactTabGraphsLoader"
                    title="Loading Impact Stats..."
                />
            )
        } else {
            impactContent = (
                <>
                    <InputSymphony
                        className="impactTabGraphsSelect"
                        isSelectDropdown={true}
                        options={[
                            {
                                value: 'total',
                                title: 'Overall Growth',
                                description: "Show overall growth for each platform"
                            },
                            {
                                value: 'daily',
                                title: 'Daily Change',
                                description: "Show the daily changes for each platform"
                            }
                        ] as SelectOptionItem[]}
                        onOptionSelect={(option: any) => {
                            setShowTotalOrDailyGraph(option.value);
                        }}
                        value={showTotalOrDailyGraph}
                    />
                    <div className="impactTabGraphs" style={{ display: 'flex', flexDirection: 'column', gap: 82 }}>
                        {impactStatsData?.map((stat: any, index: number) => {
                            const { title, icon, data } = stat;
                            const {
                                dailyStats,
                                percentageChange,
                                currentValue,
                                secondaryStatTitle,
                                secondaryStatValue
                            } = data;

                            const impactDailyStats = dailyStats.map((stat: DailyStat) => {
                                if (showTotalOrDailyGraph === 'total') {
                                    return {
                                        date: stat.datetime,
                                        value: stat.value
                                    }
                                } else {
                                    return {
                                        date: stat.datetime,
                                        value: stat.absoluteChange
                                    }
                                }
                            });
                            return (
                                <div
                                    key={makeKey(title, index)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8
                                    }}
                                >
                                    {/* <CardSymphony
                            className="impactTabGraphHeader"
                            title={title}
                            subtitle={`${title} for ${campaignDetailsData?.campaignName}`}
                            isNoPadding
                            size="small"
                        /> */}
                                    <AreaChartSymphony
                                        className="impactTabGraph"
                                        data={impactDailyStats}
                                        showLegend={false}
                                        yAxisName={title}
                                        title={title}
                                        icon={icon}
                                        currentValue={currentValue}
                                        percentageChange={hasCampaignStartedAndSpent ? percentageChange : null}
                                        rightSideTitle={hasCampaignStartedAndSpent ? secondaryStatTitle : null}
                                        rightSideValue={hasCampaignStartedAndSpent ? secondaryStatValue : null}
                                        referenceLines={referenceLines}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </>)
        }

        if (impactStatsData?.length === 0) {
            return null;
        }

        return (
            <>
                <CardSymphony
                    className="impactTabHeader_impact"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                    title="Impact Stats"
                    subtitle="These are the impact stats for your campaign."
                    isNoPadding
                    size="small"
                    isGemIcon
                />
                {/* <button onClick={() => setShowDailyVsLifetime(showDailyVsLifetime === 'total' ? 'daily' : 'total')}>View Daily vs Lifetime</button> */}
                {impactContent}
            </>
        )
    }

    const renderStreamingLinksBreakdown = () => {
        if (![PlaybookType.INCREASE_MUSIC_STREAMS, PlaybookType.GROW_PLAYLIST_FOLLOWERS].includes(playbook as PlaybookType)) {
            return null;
        }

        const {
            platformClicksBreakdown
        } = campaignAnalyticsData || {};

        if (!platformClicksBreakdown) {
            return null;
        }

        return (
            <>
                <CardSymphony
                    className="impactTabHeader_performance"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                    title="Streaming Clicks by Platform"
                    subtitle="These are the number of streaming service clicks directly tracked from Symphony ads. These stats may be lower than shown above, as they update less frequently."
                    isNoPadding
                    size="small"
                    isGemIcon
                />

                {hasAdvancedAnalytics || isAdBoxAnalyticsUnlocked ?
                    <StreamingLinksBreakdown
                        data={platformClicksBreakdown}
                    />
                    :
                    <AdBoxBlurredGraph
                        className="streamingLinksBreakdownGraph"
                        title="Track streaming clicks by platform"
                        graph={null}
                        buttonText="Upgrade to Growth"
                        upgradePlan={minRequiredPlan || PlanType.GROWTH}
                    />
                }
            </>
        )
    }

    const renderConversionsInfo = () => {
        if (!conversionsEnabled) {
            return null;
        }
        let conversionsEventTitle = `Goal: `
        let conversionEventLabel = null;
        let highlightedTrack = v2_campaign_input.highlightedTrack;
        if (!conversionsEventName) {
            return null
        }
        if (playbook === PlaybookType.INCREASE_MUSIC_STREAMS) {
            conversionEventLabel = 'Your ads will optimize to grow streams.';
        } else if (playbook === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
            conversionEventLabel = 'Your ads will optimize to grow playlist engagement.';
        } else {
            conversionsEventTitle = 'Conversion Event: '
            conversionEventLabel = `${conversionsEventName}`
        }

        return (
            <div
                className="conversionsEnabledTitleContainer"
                style={mStyle(sty, 'conversionsEnabledTitleContainer')}
            >
                <div className="conversionsEnabledContainer" style={sty.conversionsEnabledContainer}>
                    <div>
                        Performance
                    </div>
                    <TagSymphony
                        className="conversionsEnabledIconContainer"
                        style={{
                            fontWeight: 400
                        }}
                        title="Conversions Enabled"
                        icon="boost"
                        type="square"
                    />
                </div>
                <div className="goalContainer" style={sty.goalContainer}>
                    <div className="goalRow" style={sty.goalRow}>
                        <div className="goalLabel1" style={Sty.text12grey}>{conversionsEventTitle}</div>
                        <div className="goalText1" style={Sty.text12}>{conversionEventLabel}</div>
                    </div>
                    {highlightedTrack?.name &&
                        <div
                            className="goalRow"
                            style={sty.goalRow}
                        >
                            <div
                                className="goalLabel2"
                                style={Sty.text12grey}
                            >
                                Autoplay Track:
                            </div>
                            <div
                                className="goalText2"
                                style={Sty.text12}
                            >
                                {highlightedTrack.name}
                            </div>
                        </div>
                    }
                </div>
            </div>
        )
    }

    const renderMaestroBanner = () => {
        return (
            <MaestroBanner
                isAdBoxCampaign={isAdBoxCampaign}
                isAdBoxAnalyticsUnlocked={isAdBoxAnalyticsUnlocked}
                className="maestroBannerFromCampaignDetailsV2"
                campaignDetails={campaign}
            />
        )
    }

    const renderImpactTab = () => {
        let cardTitle = null

        if (conversionsEnabled) {
            cardTitle = renderConversionsInfo()
        } else {
            cardTitle = "Performance";
        }
        return (
            <TabSymphony
                className="impactTab"
                visible={selectedCampaignDetailsTab.id === 'impact'}
                style={sty.tabCampaignDetails}
            >
                {renderMaestroBanner()}
                {renderStatProgressCardsForLogistics()}
                <CardSymphony
                    className="impactTabHeader_performance"
                    title={cardTitle}
                    isNoPadding
                    size="small"
                />
                {renderTopLevelPrimaryStats()}

                {renderTopLevelSecondaryStats()}
                {renderStreamingLinksBreakdown()}
                {renderImpactTabGraphs()}
            </TabSymphony>
        )
    }

    // TODO MEGH: Re-add Markets tab
    const renderMarketsTabHeader = () => {
        if (!campaignAnalyticsData) {
            return (
                <Loader
                    className="marketsTabHeaderLoader"
                    title="Loading Markets..."
                />
            )
        }
        const { markets } = campaignAnalyticsData;

        return (
            <div
                className="marketsTabHeader"
                style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                }}
            >
                {markets.map((market: any, index: number) => {
                    return (
                        <TagSymphony
                            key={makeKey(market.name, index)}
                            className="marketsTabTag"
                            id={market.name}
                            title={market.name}
                            onClick={() => {
                                setSelectedMarket(market)
                            }}
                            color={selectedMarket?.name === market.name ? 'purple' : 'grey'}
                        />
                    )
                })}
            </div>
        )
    }

    const renderMarketStats = () => {
        return (
            <div
                className="marketStatsContainer"
                style={{
                    display: 'grid',
                    gridTemplateColumns: mobileView ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: mobileView ? 8 : 16,
                }}
            >
                {marketStats?.map((stat: StatItem, index: number) => {
                    const { title, value, displayType } = stat;
                    return (
                        <Stat
                            key={makeKey('marketStat', title, index)}
                            icon={getStatIcon(title as string)}
                            className="marketStat"
                            stats={[
                                {
                                    title: title,
                                    value: value,
                                    displayType: displayType ?? "number"
                                }
                            ]}
                        />
                    )
                })}
            </div>
        )
    }

    const renderMarketsTab = () => {

        // TODO MEGH: Re-add Markets tab
        return (
            <TabSymphony
                className="marketsTab"
                visible={selectedCampaignDetailsTab.id === 'markets'}
                style={sty.tabCampaignDetails}
            >
                <Banner
                    className="marketsTabHeaderBanner"
                    title="An upgraded Markets tab is coming shortly!"
                    color="green"
                >
                    Don't worry, it's coming soon 😍
                </Banner>
            </TabSymphony>
        )

        const { budget = 0, spent = 0 } = selectedMarket || {};
        return (
            <TabSymphony
                className="marketsTab"
                visible={selectedCampaignDetailsTab.id === 'markets'}
                style={sty.tabCampaignDetails}
            >
                {renderMarketsTabHeader()}
                <div className="marketDetailsContainer">
                    <div className="marketHeaderContainer" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="marketName" style={Sty.text24}>
                            {selectedMarket?.name}
                        </div>
                        <TagSymphony
                            className="marketStatusTag"
                            title={CAMPAIGN_STATUS_INFO[selectedMarket?.status as CampaignStatus].title}
                            color={CAMPAIGN_STATUS_INFO[selectedMarket?.status as CampaignStatus].color as any}
                            type="square"
                        />
                    </div>
                    <div className="marketStatsContainerInner" style={{ display: 'flex', gap: 4, ...Sty.text14grey }}>
                        <div>Budget remaining:</div>
                        <div className="marketBudgetRemaining">
                            ${formatNumberToDisplayType(budget - spent, 'currency')}
                        </div>
                        <div>of</div>
                        <div className="marketBudget">
                            ${formatNumberToDisplayType(budget, 'currency')}
                        </div>
                    </div>
                </div>
                {renderMarketStats()}
            </TabSymphony>
        )
    }

    const renderAudiencesTab = () => {
        /** Sample audience data for display */

        const renderAudienceStats = (audience: any) => {
            const totalImpressions = audience.stats.reduce((sum: number, stat: any) => sum + parseInt(stat.impressions), 0);
            const totalReach = audience.stats.reduce((sum: number, stat: any) => sum + parseInt(stat.reach), 0);
            const totalClicks = audience.stats.reduce((sum: number, stat: any) => sum + parseInt(stat.clicks), 0);
            const totalSpend = audience.stats.reduce((sum: number, stat: any) => sum + parseFloat(stat.spend), 0);

            const sty: Record<string, CSSProperties> = {
                statsContainer: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    marginTop: 16
                }
            };

            return (
                <div className="audienceStatsContainer" style={sty.statsContainer}>
                    <TagSymphony
                        className="impressionsTag"
                        title={`Impressions: ${totalImpressions.toLocaleString()}`}
                        type="square"
                        color="purple"
                    />
                    <TagSymphony
                        className="reachTag"
                        title={`Reach: ${totalReach.toLocaleString()}`}
                        type="square"
                    />
                    <TagSymphony
                        className="clicksTag"
                        title={`Clicks: ${totalClicks.toLocaleString()}`}
                        color="purple"
                        type="square"
                    />
                    <TagSymphony
                        className="spendTag"
                        title={`Spend: $${totalSpend.toFixed(2)}`}
                        type="square"
                        color="purple"
                    />
                </div>
            );
        };

        return (
            <TabSymphony
                className="audiencesTab"
                visible={selectedCampaignDetailsTab.id === 'audiences'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="audiencesHeader"
                    title="Targeted Audiences"
                    subtitle="These are the different audiences targeted by your campaign. These represent ad sets that were created for this campaign."
                    isNoPadding
                    size="small"

                />

                {audienceStats?.map((audience, index) => (
                    <Banner
                        key={`audience-${index}`}
                        className="audienceBanner"
                        title={audience.name}
                        color="white"
                        style={{
                            border: Sty.border,
                        }}
                    >
                        {renderAudienceStats(audience)}
                    </Banner>
                ))}
            </TabSymphony>
        );
    };

    const renderAgesAndGendersGraph = () => {
        return (
            <div className="demographicsAgesAndGendersGraph">
                <CardSymphony
                    className="demographicsAgesAndGendersGraphHeader"
                    title="Top Ages & Genders by Clicks"
                    subtitle="Here are the top ages / genders that click on your ads the most"
                    isNoPadding
                    size="small"
                />
                {renderBarChartExamples()}
            </div>
        )
    }

    const renderDemographicsTab = () => {

        if (!campaignAnalyticsData) {
            return null;
        }

        const {
            demographicsBreakdown,
            geographicBreakdown,
        } = campaignAnalyticsData;

        if (!geographicBreakdown || !demographicsBreakdown) {
            return null;
        }

        return (
            <TabSymphony
                className="demographicsTab"
                visible={selectedCampaignDetailsTab.id === 'demographics'}
                style={sty.tabCampaignDetails}
            >
                <DemographicsStats
                    className="demographicsStatsCampaignDetailsV2"
                    geographicBreakdown={geographicBreakdown}
                    demographicsBreakdown={demographicsBreakdown}
                    currency={currency?.code}
                    isLoadingDemographics={isLoadingCampaignAnalytics}
                    isAdBoxUnlocked={isAdBoxAnalyticsUnlocked}
                    onUnlockAdBox={handleUnlockAdBoxAnalytics}
                />
            </TabSymphony>
        )
    }

    // TODO MEGH: Re-add Daily stats
    const renderDailyStatsGraph = (dailyStatInfo: any) => {
        if (!campaignAnalyticsData) {
            return null;
        }

        const { dailyStats } = campaignAnalyticsData;
        const { key, title, description } = dailyStatInfo;

        const currentDailyStats = dailyStats.map((stat: any) => {
            return {
                date: stat.date, // NOTE: this will be like "01/20"
                value: stat[key]
            }
        });

        return (
            <div className="dailyStatsTotalSpendGraph">
                <CardSymphony
                    className="dailyStatsGraphHeader"
                    title={title}
                    subtitle={description}
                    isNoPadding
                    size="small"
                    style={{ marginBottom: 16 }}
                    isGemIcon
                />
                {/* <DebugBox
                    alwaysShow
                    data={{
                        currentDailyStats
                    }}
                /> */}
                {!(hasAdvancedAnalytics || isAdBoxAnalyticsUnlocked) ?
                    <AdBoxBlurredGraph
                        className="dailyStatsTotalSpendGraphBlurredGraph"
                        title="Track your daily campaign performance"
                        graph={null}
                        buttonText="Upgrade to Growth"
                        upgradePlan={minRequiredPlan || PlanType.GROWTH}
                    />
                    :
                    <AreaChartSymphony
                        className="dailyStatsTotalSpendGraph"
                        data={currentDailyStats}
                        showLegend={false}
                        yAxisName={title}
                    // NOTE CONNER: the tooltip comes from here `<GraphTooltip`
                    />
                }
            </div>
        )
    }

    const renderDailyStatsTab = () => {
        let dailyStatsGraphs = getDailyStatsInfo(playbook).map((dailyStatInfo, index) => {
            const { key } = dailyStatInfo;
            return (
                <Fragment key={makeKey(key, index)}>
                    {renderDailyStatsGraph(dailyStatInfo)}
                </Fragment>
            )
        })

        return (
            <TabSymphony
                className="dailyStatsTab"
                visible={selectedCampaignDetailsTab.id === 'daily_stats'}
                style={styx(sty.tabCampaignDetails, { gap: 82 })}
            >
                {dailyStatsGraphs}
            </TabSymphony>
        )
    }

    const renderAssetsTab = () => {
        const { assetPerformanceAnalytics } = campaignAnalyticsData || {};

        const isNotEditable = adPlatform === AdPlatform.TIKTOK || campaignStatus === CampaignStatus.FINISHED || isAdBoxCampaign || usesExpressSetup || campaignStatus === CampaignStatus.ARCHIVED;

        if (!assetPerformanceAnalytics || assetPerformanceAnalytics.length === 0) {
            return (
                <TabSymphony
                    className="assetsTab"
                    visible={selectedCampaignDetailsTab.id === 'assets'}
                    style={sty.tabCampaignDetails}
                >
                    {adPlatform === AdPlatform.TIKTOK ?
                        <Banner
                            className="noAssetsBanner"
                            title="Asset Performance Data is Coming Soon!"
                            color="green"
                        >
                            Check back tomorrow to see your asset performance data.
                        </Banner>
                        :
                        isNotEditable ?
                            <CardSymphony
                                className="noAssetsCard"
                                title="No Asset Performance Data"
                                subtitle="There is currently no asset performance data available for this campaign."
                                size="small"
                            >
                                <ButtonSymphony
                                    className="uploadNewAssetsButton"
                                    variant="primary"
                                    size="small"
                                    disabled={!canUploadAssets(campaign?.status) || isUploadingAssets || usesExpressSetup}
                                    onClick={() => {
                                        if (usesExpressSetup) {
                                            setShowUploadAssetsModal(true);
                                        } else if (canUploadAssets(campaign?.status)) {
                                            setShowUploadAssetsModal(true);
                                        } else {
                                            setShowInvalidStatusModal(true);
                                        }
                                    }}
                                >
                                    Upload new assets
                                </ButtonSymphony>
                            </CardSymphony>
                            :
                            null
                    }

                </TabSymphony>
            );
        }

        // Check if campaign is a supported type for advanced asset features
        const campaignAllowsAssetEditing = adPlatform === AdPlatform.META && !usesExpressSetup;

        /** 
         * Sort assets by the selected metric
         * This prioritizes showing the best performing assets at the top
         */
        const sortedAssetPerformanceAnalytics = getSortedAssetPerformanceAnalytics();

        return (
            <TabSymphony
                className="assetsTab"
                visible={selectedCampaignDetailsTab.id === 'assets'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="assetsHeader"
                    title="Asset Performance"
                    subtitle="Performance metrics for each creative asset used in your campaign"
                    isNoPadding
                    size="small"
                    isGemIcon
                />

                {adPlatform === AdPlatform.META && (
                    <div className="assetsTabControls" style={mStyle(sty, 'assetsTabControls')}>
                        <InputSymphony
                            className="assetSortSelect"
                            isSelectDropdown={true}
                            style={mStyle(sty, 'assetSortSelect')}
                            options={[
                                {
                                    value: 'conversions',
                                    title: 'Sort by Conversions',
                                    description: "Show assets with highest conversions first"
                                },
                                {
                                    value: 'clicks',
                                    title: 'Sort by Clicks',
                                    description: "Show assets with highest clicks first"
                                },
                                {
                                    value: 'spend',
                                    title: 'Sort by Spend',
                                    description: "Show assets with highest spend first"
                                },
                                {
                                    value: 'cpc',
                                    title: 'Sort by Cost-per-click',
                                    description: "Show assets with lowest cost-per-click first"
                                },
                                {
                                    value: 'cpm',
                                    title: 'Sort by CPM',
                                    description: "Show assets with highest CPM first"
                                },
                                {
                                    value: 'impressions',
                                    title: 'Sort by Impressions',
                                    description: "Show assets with highest impressions first"
                                },
                                {
                                    value: 'reach',
                                    title: 'Sort by Reach',
                                    description: "Show assets with highest reach first"
                                }
                            ] as SelectOptionItem[]}
                            onOptionSelect={(option: any) => {
                                setAssetSortMetric(option.value);
                            }}
                            value={assetSortMetric}
                        />
                        <ButtonSymphony
                            className="uploadNewAssetsButton"
                            variant="primary"
                            size="small"
                            disabled={!canUploadAssets(campaign?.status) || isUploadingAssets || usesExpressSetup}
                            onClick={() => {
                                if (usesExpressSetup) {
                                    setShowUploadAssetsModal(true);
                                } else if (canUploadAssets(campaign?.status)) {
                                    setShowUploadAssetsModal(true);
                                } else {
                                    setShowInvalidStatusModal(true);
                                }
                            }}
                        >
                            Upload new assets
                        </ButtonSymphony>
                    </div>
                )}

                {!(hasAdvancedAnalytics || isAdBoxAnalyticsUnlocked) ?
                    <AdBoxBlurredGraph
                        className="assetsTabBlurredGraph"
                        title="Track performance of each creative asset"
                        graph={null}
                        buttonText="Upgrade to Growth"
                        upgradePlan={minRequiredPlan || PlanType.GROWTH}
                    />
                    :
                    <div className="assetsTabContainer" style={sty.assetsTabContainer}>
                        {sortedAssetPerformanceAnalytics.map((asset, index) => {
                            const {
                                assetId,
                                thumbnailUrl,
                                originalPostType,
                                mediaType,
                                stats,
                                permalinkUrl,
                                previewLink,
                                isIneligible,
                                statsArray,
                                ineligible_reason,
                                creativeId,
                                paused,
                            } = asset;

                            const showBestPerformingTag = index === 0 && stats?.spend > 0;

                            return (
                                <div
                                    key={makeKey('asset', assetId, index)}
                                    className="assetPerformanceCardBanner"
                                    style={mStyle(sty, 'assetPerformanceCardBanner')}
                                    color="white"
                                >
                                    {thumbnailUrl &&
                                        <div
                                            className="assetThumbnail"
                                            style={styx(
                                                mStyle(sty, 'assetThumbnail'),
                                                {
                                                    backgroundImage: `url(${thumbnailUrl})`,
                                                    backgroundColor: Colors.greyLight,
                                                    opacity: paused ? 0.5 : 1,
                                                }
                                            )}
                                        />
                                    }
                                    <div
                                        className="assetPerformanceCardContent"
                                        style={mStyle(sty, 'assetPerformanceCardContent')}
                                    >
                                        <div
                                            className="assetPerformanceCardContentInner"
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                flexDirection: mobileView ? 'column' : 'row'
                                            }}
                                        >
                                            <div
                                                className="assetPerformanceCardTitleContainer"
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    marginBottom: mobileView ? 8 : 0
                                                }}
                                            >
                                                <div
                                                    className="assetPerformanceCardTitle"
                                                    style={Sty.text18bold}
                                                >
                                                    Ad #{index + 1}
                                                </div>
                                                {isIneligible &&
                                                    <TagSymphony
                                                        key={makeKey('ineligible', assetId)}
                                                        className="goalsCategoryTag"
                                                        title={ineligible_reason || 'This post is ineligible to run for ads because it includes an event'}
                                                        color='yellow'
                                                    />
                                                }
                                                {paused &&
                                                    <TagSymphony
                                                        key={makeKey('paused', assetId)}
                                                        className="pausedAssetTag"
                                                        title="Paused"
                                                        color={CAMPAIGN_STATUS_INFO[CampaignStatus.PAUSED].color as any}
                                                        size="small"
                                                    />
                                                }
                                                {showBestPerformingTag &&
                                                    <TagSymphony
                                                        key={makeKey('best', assetId)}
                                                        className="bestPerformingTag"
                                                        title="Best Performing"
                                                        color="green"
                                                    />
                                                }
                                            </div>
                                            <div
                                                className="assetPerformanceCardButtonsContainer"
                                                style={mStyle(sty, 'assetPerformanceCardButtonsContainer')}
                                            >
                                                {campaignAllowsAssetEditing && !isIneligible && creativeId && (
                                                    <ButtonSymphony
                                                        className="pauseResumeAssetButton"
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handlePauseResumeAsset(campaignId, creativeId, !paused)}
                                                        isLoading={isPausingAsset === creativeId}
                                                        disabled={isPausingAsset === creativeId}
                                                    >
                                                        {paused ? 'Resume' : 'Pause'}
                                                    </ButtonSymphony>
                                                )}
                                                {permalinkUrl && !isIneligible &&
                                                    <ButtonSymphony
                                                        className="previewButton1"
                                                        variant="link"
                                                        href={permalinkUrl}
                                                        target="_blank"
                                                    >
                                                        View Ad
                                                    </ButtonSymphony>
                                                }
                                                {!permalinkUrl && previewLink &&
                                                    <ButtonSymphony
                                                        className="previewButton2"
                                                        size="small"
                                                        variant="link"
                                                        href={previewLink}
                                                        target="_blank"
                                                    >
                                                        Preview
                                                    </ButtonSymphony>
                                                }
                                            </div>
                                        </div>
                                        <div
                                            className="assetPerformanceCardStats"
                                            style={{
                                                ...Sty.text14grey,
                                                marginBottom: 4
                                            }}
                                        >
                                            {originalPostType} • {mediaType}
                                        </div>
                                        <div
                                            className="assetPerformanceCardId"
                                            style={{
                                                ...Sty.text14grey,
                                                marginBottom: 4
                                            }}
                                        >
                                            ID: {assetId}
                                        </div>

                                        <div className="statsContainer" style={mStyle(sty, 'statsContainer')}>
                                            {(statsArray || []).map((stat: any, statIndex: number) => {
                                                const { name, value, displayType } = stat;
                                                return (
                                                    <TagSymphony
                                                        key={`asset-${assetId}-stat-${statIndex}`}
                                                        className={`${name.toLowerCase().replace(/\s+/g, '')}Tag`}
                                                        title={`${name}: ${formatNumberToDisplayType(value, displayType)}`}
                                                        type="square"
                                                        color={statIndex % 2 === 0 ? 'purple' : undefined}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                }
            </TabSymphony>
        );
    };

    const renderSetupDetailsTabItem = ({
        icon,
        title,
        items = [],
        shareUrl,
        customContent,
    }: {
        icon: string;
        title: string;
        items?: Array<{ label: string; value: React.ReactNode }>;
        shareUrl?: string;
        customContent?: React.ReactNode;
    }) => {
        return (
            <div
                className="setupDetailCard"
                style={sty.setupDetailCard}
            >
                <div className="setupDetailCardHeader" style={sty.setupDetailCardHeader}>
                    <div className="setupDetailIconContainer" style={sty.setupDetailIconContainer}>
                        <Icon
                            className="setupDetailIcon"
                            icon={icon}
                            size={24}
                        />
                    </div>
                    <div className="setupDetailCardTitle" style={Sty.text18bold}>
                        {title}
                    </div>
                    {shareUrl && (
                        <Icon
                            className="shareIcon"
                            icon="share"
                            size={16}
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(shareUrl, '_blank')}
                        />
                    )}
                </div>
                <div className="setupDetailCardContent" style={sty.setupDetailCardContent}>
                    {items.map((item, index) => (
                        <div
                            key={`item-${index}-${item.label}`}
                            className="setupDetailCardItem"
                            style={sty.setupDetailCardItem}
                        >
                            <div className="setupDetailCardItemLabel" style={Sty.text14grey}>
                                {item.label}
                            </div>
                            <div className="setupDetailCardItemValue" style={Sty.text14}>
                                {item.value}
                            </div>
                        </div>
                    ))}
                    {customContent}
                </div>
            </div>
        );
    };

    const renderSetupDetailsTab = () => {
        // Only show this tab for Meta and TikTok campaigns
        if (adPlatform !== AdPlatform.META && adPlatform !== AdPlatform.TIKTOK) {
            return (
                <TabSymphony
                    className="setupDetailsTab"
                    visible={selectedCampaignDetailsTab.id === 'setup_details'}
                    style={sty.tabCampaignDetails}
                >
                    <Banner
                        className="setupDetailsTabBanner"
                        title="Setup details are only available for Meta and TikTok campaigns."
                        color="yellow"
                    />
                </TabSymphony>
            );
        }

        const {
            fbPage,
            igPage,
            fbAdAccount,
            conversionsEnabled: campaignConversionsEnabled,
            conversionsEventName: campaignConversionsEventName
        } = v2_campaign_input || {};

        const { platform_details } = campaign.campaign_metadata || {};

        // Campaign details custom content
        const campaignCustomContent = platform_details?.campaigns?.length > 0 ? (
            platform_details.campaigns.map((campaign: { id: string; name: string }, index: number) => (
                <div
                    key={`campaign-${campaign.id}`}
                    className="setupDetailCardItem"
                    style={{ ...sty.setupDetailCardItem, justifyContent: 'space-between' }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div className="setupDetailCardItemLabel" style={Sty.text14grey}>
                            Campaign {index + 1}
                        </div>
                        <div className="setupDetailCardItemValue" style={Sty.text14}>
                            {campaign.name}
                        </div>
                        <div className="setupDetailCardItemValue" style={Sty.text12grey}>
                            ID: {campaign.id}
                        </div>
                    </div>
                    <Icon
                        className="shareIcon"
                        icon="share"
                        size={16}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            window.open(`https://www.facebook.com/adsmanager/manage/campaigns?act=${platform_details.ad_account.replace('act_', '')}&selected_campaign_ids=${campaign.id}`, '_blank');
                        }}
                    />
                </div>
            ))
        ) : mainError ? (
            <div className="setupDetailCardItem" style={sty.setupDetailCardItem}>
                <div className="setupDetailCardItemValue" style={{ ...Sty.text14, color: Colors.yellow }}>
                    Please review your campaign details to understand any actions needed for error resolution.
                </div>
            </div>
        ) : (
            <div className="setupDetailCardItem" style={sty.setupDetailCardItem}>
                <div className="setupDetailCardItemValue" style={Sty.text14}>
                    No campaigns made in Facebook yet. They should be created soon.
                </div>
            </div>
        );

        // these campaigns don't have a promoted link
        const hasNoLinkToShow = [PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT].includes(playbook as PlaybookType);

        return (
            <TabSymphony
                className="setupDetailsTab"
                visible={selectedCampaignDetailsTab.id === 'setup_details'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="setupDetailsHeader"
                    title="Campaign Setup Details"
                    subtitle="These are the connection details used for this campaign"
                    isNoPadding
                    size="small"
                />
                <div className="setupDetailsContainer" style={sty.setupDetailsContainer}>
                    {/* Promoted Link Details - show for both Meta and TikTok */}
                    {!hasNoLinkToShow && v2_campaign_input?.linksToPromote && v2_campaign_input.linksToPromote.length > 0 && (
                        renderSetupDetailsTabItem({
                            icon: 'link',
                            title: 'Promoted Link',
                            items: [
                                {
                                    label: 'URL',
                                    value: v2_campaign_input.linksToPromote[0].url || 'Not specified'
                                }
                            ]
                        })
                    )}

                    {/* Only show Meta-specific details for Meta campaigns */}
                    {adPlatform === AdPlatform.META && (
                        <>
                            {/* Conversion Details */}
                            {renderSetupDetailsTabItem({
                                icon: 'boost',
                                title: 'Conversion Settings',
                                items: [
                                    {
                                        label: 'Conversions Enabled',
                                        value: campaignConversionsEnabled ? 'Yes' : 'No'
                                    },
                                    ...(campaignConversionsEnabled ? [{
                                        label: 'Conversion Event',
                                        value: campaignConversionsEventName || 'Not specified'
                                    }] : [])
                                ]
                            })}

                            {/* Only show Facebook-specific details for non-express setup campaigns */}
                            {!usesExpressSetup && (
                                <>
                                    {/* Campaign Details */}
                                    {renderSetupDetailsTabItem({
                                        icon: 'facebook',
                                        title: 'Campaigns',
                                        customContent: campaignCustomContent
                                    })}

                                    {/* Ad Account Details */}
                                    {renderSetupDetailsTabItem({
                                        icon: 'facebook',
                                        title: 'Ad Account',
                                        shareUrl: platform_details?.ad_account ?
                                            `https://www.facebook.com/adsmanager/manage/adaccounts?act=${platform_details.ad_account.replace('act_', '')}` :
                                            undefined,
                                        items: [
                                            {
                                                label: 'Name',
                                                value: fbAdAccount?.name || 'Not specified'
                                            },
                                            {
                                                label: 'ID',
                                                value: fbAdAccount?.id || 'Not specified'
                                            },
                                            ...(fbAdAccount?.currency ? [{
                                                label: 'Currency',
                                                value: fbAdAccount.currency
                                            }] : [])
                                        ]
                                    })}

                                    {/* Facebook Page Details */}
                                    {renderSetupDetailsTabItem({
                                        icon: 'facebook',
                                        title: 'Facebook Page',
                                        items: [
                                            {
                                                label: 'Name',
                                                value: fbPage?.name || 'Not specified'
                                            },
                                            {
                                                label: 'ID',
                                                value: fbPage?.id || 'Not specified'
                                            }
                                        ]
                                    })}

                                    {/* Instagram Page Details */}
                                    {renderSetupDetailsTabItem({
                                        icon: 'instagram',
                                        title: 'Instagram Page',
                                        items: [
                                            {
                                                label: 'Username',
                                                value: igPage?.instagramAccount?.username || 'Not specified'
                                            },
                                            {
                                                label: 'ID',
                                                value: igPage?.instagramAccount?.id || 'Not specified'
                                            }
                                        ]
                                    })}
                                </>
                            )}
                        </>
                    )}
                </div>
            </TabSymphony>
        );
    };

    const handlePauseResumeAsset = async (campaignId: number, creativeId: string, pause: boolean) => {
        if (usesExpressSetup) {
            setShowExpressSetupModal(true);
            return;
        }

        try {
            setIsPausingAsset(creativeId);
            await pauseResumeAsset(campaignId, creativeId, pause);
            toastSuccess(`Asset ${pause ? 'paused' : 'resumed'} successfully`);
            // Refresh campaign analytics to update asset status
            getCampaignAnalytics(campaignId);
        } catch (error) {
            console.error('Error pausing/resuming asset:', error);
            toastError(`Failed to ${pause ? 'pause' : 'resume'} asset`);
        } finally {
            setIsPausingAsset(null);
        }
    };

    const renderCampaignDetailsTabs = () => {
        return (
            <>
                {renderImpactTab()}
                {renderMarketsTab()}
                {renderAudiencesTab()}
                {renderDemographicsTab()}
                {renderAssetsTab()}
                {renderDailyStatsTab()}
                {renderSetupDetailsTab()}
            </>
        )
    }

    const renderAdPreview = () => {
        // Get username with fallbacks
        const username = v2_campaign_input?.igPage?.businessAccount?.username
            || v2_campaign_input?.igPage?.instagramAccount?.username
            || v2_campaign_input?.fbPage?.name
            || currentBrand?.name
            || '';

        // Get CTA with fallback
        const callToActionLabel = v2_campaign_input?.cta?.label || 'Listen Now';

        let asset;
        if (adPlatform === AdPlatform.META) {
            asset = getSortedAssetPerformanceAnalytics()?.[0] as any;
        } else {
            asset = v2_campaign_input?.assets?.find((asset: CreativeSelectorPost) => asset.assetUrl && asset.assetUrl.includes('https://'))
        }

        let topPerformingPlacement = 'ig-reels';
        if (creativePerformanceStats?.length) {
            const sortedStats = [...creativePerformanceStats].sort((a, b) => b.percentage - a.percentage);
            const bestPerforming = sortedStats[0].title;

            if (bestPerforming === 'Reels') {
                topPerformingPlacement = 'ig-reels';
            } else if (bestPerforming === 'Stories') {
                topPerformingPlacement = 'ig-story';
            } else if (bestPerforming === 'IG Posts') {
                topPerformingPlacement = 'ig-feed';
            }
        }

        const captions: Caption[] = v2_campaign_input?.captions?.length > 0 ?
            v2_campaign_input?.captions
            :
            [{ text: asset?.title }]

        const adPreviewComponent = (
            <AdPreview
                className="campaignDetailsV2AdPreview"
                style={{
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    padding: 0,
                    boxShadow: 'none'
                }}
                username={username}
                callToActionLabel={callToActionLabel}
                profilePicUrl={null}
                previewedAsset={{
                    id: asset?.id || '',
                    assetUrl: asset?.assetUrl || '',
                    title: asset?.title || '',
                    platformCaption: asset?.platformCaption || '',
                    timestamp: asset?.timestamp || new Date().toISOString(),
                    originalPostType: asset?.originalPostType as AssetPlatformType || AssetPlatformType.INSTAGRAM,
                    mediaType: asset?.mediaType as MediaType || MediaType.VIDEO
                }}
                captions={captions}
                previewType={topPerformingPlacement}
            />
        )
        if (mobileView) {
            return (
                <div
                    className="campaignDetailsV2AdPreviewMobileContainerMobile"
                    style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}
                >
                    {adPreviewComponent}
                </div>
            )
        }
        return adPreviewComponent
    }

    const renderRightSideContent = () => {
        const {
            creativePerformanceStats,
        } = campaignAnalyticsData || {};

        const isCreativePerformanceStatsExists = creativePerformanceStats && creativePerformanceStats?.length > 0;

        return (
            <div
                className="campaignDetailsRightSideContent"
                style={styx(
                    mStyle(sty, 'campaignDetailsRightSideContent'),
                    !campaignAnalyticsData && { backgroundColor: 'white' }
                )}
            >
                <CardSymphony
                    className="creativePerformanceHeader"
                    style={sty.creativePerformanceHeader}
                    title={getAdCreativeTitle()}
                    subtitle={getAdCreativeSubtitle()}
                    size="small"
                    isNoPadding
                >
                    {/* NOTE CONNER: this opens up the post in instagram, but Bo told me it's a different link, something more involved to share ad or something
                        <Icon
                            className="creativePerformanceStatsButton"
                            icon="instagram"
                            onClick={() => {
                                window.open(`https://www.instagram.com/${v2_campaign_input?.igPage?.instagramAccount?.username}/`, '_blank');
                            }}
                        /> */}
                    {isCreativePerformanceStatsExists &&
                        <div
                            className="creativePerformanceStatsContainer"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                textAlign: 'center'
                            }}
                        >
                            {creativePerformanceStats?.map((statObject: StatItem, index: number) => {
                                const { title, value } = statObject;
                                return (
                                    <div key={makeKey(title, index)}>
                                        <div
                                            className="creativePerformanceStatValue"
                                            style={styx(
                                                Sty.text14bold,
                                                { marginBottom: -4 }
                                            )}
                                        >
                                            {value}
                                        </div>
                                        <div
                                            className="creativePerformanceStatTitle"
                                            style={Sty.text12grey}
                                        >
                                            {title}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    }
                </CardSymphony>
                {renderAdPreview()}
            </div>
        )
    }

    const renderBarChartExamples = () => {
        // use this componnet:
        // src/pages/MarketingPage/CampaignDetails/DemographicsStats.tsx
        return null
    }

    const renderCampaignStatusBanner = () => {
        let title = null;
        let description = null;
        let description2 = null;
        let bannerColor = 'green';
        let actionText: string | false = false;
        let actionFunction: (() => void) | null = null;
        let hasBeenExtended = daysExtended && daysExtended > 0;
        let reportedStartDate = finalStartDate || startDate;
        let reportedEndDate = finalEndDate || endDate;
        let userInterventionNotNeeded = true;

        // Check if the campaign has finished
        let isAfterFinishDate = dayjs().isAfter(dayjs(reportedEndDate));
        let hasSpentMoney = budget?.spent && budget?.spent > 0;

        let showErrorToUsers = mainError?.showToUsers;

        if (showErrorToUsers) {
            title = mainError.title;
            description = mainError.description;
            actionText = mainError.ctaText;
            bannerColor = 'yellow';
            userInterventionNotNeeded = mainError.canRetry;

            // Handle different CTA actions
            // ctaAction allows errors to specify actions instead of just links
            // Currently supports: 'uploadAsset' to trigger asset upload modal
            if (mainError.ctaAction === 'uploadAsset') {
                actionFunction = () => {
                    if (canUploadAssets(campaign?.status)) {
                        setShowUploadAssetsModal(true);
                    } else {
                        setShowInvalidStatusModal(true);
                    }
                };
            } else if (mainError.ctaLink) {
                actionFunction = () => {
                    window.open(mainError.ctaLink, '_blank');
                };
            } else {
                actionFunction = () => {
                    Intercom.showNewMessage(`My campaign with ID ${campaignId} has an error. Please help me out!`);
                };
            }
        } else {

            let campaignExtendedDescription = '';
            if (hasBeenExtended) {
                campaignExtendedDescription = ` Please note that this campaign started on ${dayjs(reportedStartDate).format('MM/DD')}, and has been extended by ${daysExtended} days to ${dayjs(reportedEndDate).format('MM/DD')}.`
            }
            switch (campaignStatus) {
                case CampaignStatus.AWAITING_AGENT_PROCESSING:
                    title = `AI Agent Processing`;
                    description = `Our AI agent is processing your campaign, and you'll receive an update when it's been properly submitted to ads platforms.`;
                    bannerColor = 'yellow';
                    break;
                case CampaignStatus.AWAITING_PAYMENT:
                case CampaignStatus.PROCESSING:
                    title = `Your campaign is being processed!`;
                    description = `Your campaign has been submitted, and your payment is being processed. Once your payment is successfully processed, your campaign will be submitted directly.`;
                    break;
                case CampaignStatus.TO_RETRY:
                    // Check for uncaught Facebook API errors
                    if (mainError?.code) {
                        title = `Your campaign is running into issues going live.`;
                        const errorMessage = mainError?.raw_error?.error_user_title || mainError?.raw_error?.message;
                        description = `It looks like there was an error from Facebook's side when submitting your campaign. Please contact support and we'll help you resolve this. ${errorMessage ? `The FB error message is:` : ''}`;
                        description2 = errorMessage;
                        bannerColor = 'yellow';
                        actionText = 'Contact Support';
                        actionFunction = () => {
                            Intercom.showNewMessage(`My campaign with ID ${campaignId} is having issues going live. The error message is: ${errorMessage}. Please help me resolve this!`);
                        };
                    } else {
                        title = `Your campaign is being processed!`;
                        description = `Your campaign has been submitted, and our automated system is reviewing it. Once your campaign goes live, you'll be notified and see it in your ads manager.`;
                    }
                    break;
                case CampaignStatus.IN_REVIEW:
                    title = `Your campaign is in review!`;
                    description = `Your campaign has been submitted and is now awaiting review from ${adPlatform === AdPlatform.TIKTOK ? `TikTok's` : `Facebook's`} ads platform.`;
                    if (hasBeenExtended) {
                        description += campaignExtendedDescription
                    }
                    break;
                case CampaignStatus.PAUSED:
                    // TODO MEGH: Handle scenario where end date has passed and its paused
                    if (!isAfterFinishDate) {
                        title = `Your campaign is currently paused.`
                        description = `You can resume your campaign to start it again. It's currently slated to end on ${dayjs(reportedEndDate).format('MMM DD')}.`;
                    }
                    break;
                case CampaignStatus.ACTIVE:
                    // Only show banner for first 24 hours after campaign goes live
                    if (dayjs().diff(dayjs(reportedStartDate), 'hours') <= 24) {
                        title = `Your campaign just went live!`
                        description = `As ads start running, you'll see your campaign's performance in realtime.`
                        if (hasBeenExtended) {
                            description += campaignExtendedDescription
                        }
                    }

                    break;
                case CampaignStatus.FINISHED:
                    if (hasSpentMoney) {
                        title = `Your campaign has finished!`
                        description = `Your analytics are updated with final stats. Please reach out to our team if you have any questions or need assistance on next steps.`
                    } else {
                        title = `Your campaign has finished!`
                        description = `It looks like your campaign end date has passed, but it hasn't spent any money. Please reach out to our team and we're happy to help you out, provide you a discount, and ensure your next campaign is successful.`
                        actionText = `Contact Support`
                        actionFunction = () => Intercom.showNewMessage(`My campaign with ID ${campaignId} has finished, but it hasn't spent any money. Please help me out!`)
                    }
                    break;
                default:
                    return null;
            }
        }


        if (!title) {
            return null
        }

        const campaignCreationErrorCodes = [
            CampaignCreationErrorCodes.FB_ASPECT_RATIO_NOT_SUPPORTED,
            CampaignCreationErrorCodes.FB_VIDEO_INELIGIBLE_TO_BOOST,
        ]
        const campaignNeedsNewAssets = campaignCreationErrorCodes.includes(mainError?.id)
        return (
            <Banner
                className="campaignStatusBanner"
                style={styx(
                    { width: '100%' },
                    mobileView && { borderRadius: 0 }
                )}
                color={bannerColor}
                title={title}
            >
                <div className="campaignStatusBannerDescription">{description}</div>
                {description2 && <div className="campaignStatusBannerDescription">{description2}</div>}
                {/* AI thoughts dropdown for agent campaigns with reasoning */}
                {campaign?.is_agent_campaign && 
                 campaign?.agent_notes?.reasoning_summary && (
                    <div style={{ marginTop: 12, marginBottom: 8 }}>
                        <ButtonSymphony
                            className="aiThoughtsToggleButton"
                            onClick={() => setIsAiThoughtsExpanded(!isAiThoughtsExpanded)}
                            variant="text"
                            iconLeft={<Icon icon={isAiThoughtsExpanded ? "chevronUp" : "chevronDown"} size={16} className="expandIcon" />}
                            style={{ alignSelf: 'flex-start', padding: '4px 8px', fontSize: '14px' }}
                        >
                            {isAiThoughtsExpanded ? 'Hide your AI\'s thoughts' : 'View your AI\'s thoughts'}
                        </ButtonSymphony>

                        {isAiThoughtsExpanded && (
                            <div 
                                className="aiThoughtsContent" 
                                style={{ 
                                    marginTop: 8, 
                                    padding: '12px', 
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                                    borderRadius: '8px', 
                                    fontSize: '14px', 
                                    lineHeight: '1.4',
                                    color: 'rgba(255, 255, 255, 0.9)'
                                }}
                            >
                                {campaign.agent_notes.reasoning_summary}
                            </div>
                        )}
                    </div>
                )}

                <div
                    className="campaignBannerButtons"
                    style={mStyle(sty, 'campaignBannerButtons')}
                >
                    {actionText && actionFunction && (
                        <ButtonSymphony
                            className="campaignStatusBannerActionButton"
                            onClick={actionFunction}
                        >
                            {actionText}
                        </ButtonSymphony>
                    )}
                    {/* NOTE Conner, Mike, Daniel: we changed the canRetry variable name to userInterventionNotNeeded because we felt like it made more sense when reading on the frontend, but changing the variable in the backend is a lot of work  */}
                    {userInterventionNotNeeded ?
                        null
                        :
                        <>
                            {!campaignNeedsNewAssets && (
                                <ButtonSymphony
                                    className="campaignStatusBannerRetryButton"
                                    variant="outlined"
                                    onClick={handleRetryCampaign}
                                    isLoading={isRetryingCampaign || isPausingResumingCampaign}
                                    disabled={isRetryingCampaign || isPausingResumingCampaign}
                                >
                                    I've made this change
                                </ButtonSymphony>
                            )}
                            <ButtonSymphony
                                className="campaignStatusBannerContactSupportButton"
                                variant="outlined"
                                onClick={showNewMessageIntercom}
                            >
                                Contact Support
                            </ButtonSymphony>
                        </>
                    }
                </div>
            </Banner>
        )
    }

    // NOTE: Unused for now
    const renderStatProgressCardsForLogistics = () => {
        const isNotEditable = adPlatform === AdPlatform.TIKTOK || campaignStatus === CampaignStatus.FINISHED || isAdBoxCampaign || usesExpressSetup || campaignStatus === CampaignStatus.ARCHIVED;

        // add 1 day to the end date to account for the fact that the campaign ends at midnight of the last day
        const remainingDays = Math.max(0, dayjs(campaignEndDate).add(1, 'days').diff(dayjs(), 'days'));
        const totalDays = dayjs(campaignEndDate).add(1, 'days').diff(dayjs(campaignStartDate), 'days');

        let lengthSubtitle = `${remainingDays} days left`
        if (remainingDays === 1) {
            lengthSubtitle = `1 day left`
        } else if (remainingDays === 0) {
            lengthSubtitle = `Ends today`
        }

        if (campaignStatus === CampaignStatus.FINISHED) {
            lengthSubtitle = `Ended on ${dayjs(campaignEndDate).format('MMM DD')}`
        }

        return (
            <div
                className="topLevelSecondaryStats"
                style={{
                    display: 'grid',
                    gridTemplateColumns: mobileView ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
                    gap: 8,
                    marginBottom: 16,
                }}
            >
                <StatProgress
                    className="campaignDetailsRightSideStatProgress1"
                    title="Budget"
                    value={budget?.total || v2_campaign_input?.budget}
                    subtitle1="Spent"
                    value1={budget?.spent}
                    icon={isNotEditable ? undefined : 'edit'}
                    onClick={() => setShowEditBudgetModal(true)}
                    displayType="currency"
                    style={styx(
                        mobileView ? { borderRadius: 0 } : {},
                        { border: Sty.border }
                    )}
                />
                <StatProgress
                    className="campaignDetailsRightSideStatProgress2"
                    title="Length"
                    value={lengthSubtitle}
                    subtitle1="Start"
                    value1={dayjs(campaignStartDate).format('MMM DD')}
                    subtitle2="End"
                    value2={dayjs(campaignEndDate).format('MMM DD')}
                    icon={isNotEditable ? undefined : 'edit'}
                    onClick={() => setShowEditEndDateModal(true)}
                    remainingDays={remainingDays}
                    totalDays={totalDays}
                    displayType="date"
                    style={styx(
                        mobileView ? { borderRadius: 0 } : {},
                        { border: Sty.border }
                    )}
                />
            </div>
        )
    }

    const renderMainContent = () => {
        if (!campaignAnalyticsData || isLoadingCampaignAnalytics) {
            return (
                <Loader
                    className="mainContentLoader"
                    title="Loading Campaign Data..."
                    style={{
                        width: '100%',
                    }}
                    size={44}
                    isCentered
                />
            )
        }

        return (
            <div
                className={clsx('campaignDetailsV2Component', className)}
                style={styx(
                    mStyle(sty, 'campaignDetailsV2Component'),
                )}
            >
                <div
                    className="campaignDetailsV2Inner"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        width: '100%',
                    }}
                >
                    {renderCampaignStatusBanner()}
                    <CardSymphony
                        className="campaignDetailsV2Card"
                        style={mStyle(sty, 'campaignDetailsV2Card')}
                    >
                        <TabsSymphony
                            className="campaignDetailsV2Tabs"
                            tabs={mainTabs}
                            selectedTab={selectedCampaignDetailsTab?.id || mainTabs[0].id}
                            onChange={(tab) => handleTabChange(tab as TabItem)}
                            isBorderBottom
                        />
                        {renderCampaignDetailsTabs()}
                    </CardSymphony>
                </div>
                {renderRightSideContent()}
            </div>
        )
    }

    /** Handle uploading new assets to the campaign */
    const handleUploadNewAssets = async () => {
        if (usesExpressSetup) {
            // For express setup campaigns, we just close the modal
            setShowUploadAssetsModal(false);
            return;
        }

        try {
            // Get selected posts from the campaign builder
            const selectedPosts = getAllSelectedPosts('facebook');

            if (selectedPosts.length === 0) {
                toastError('Please select at least one asset to upload');
                return;
            }

            setIsUploadingAssets(true);

            // Call the API to upload the new assets
            await uploadNewAssets(campaignId, selectedPosts);

            // Close modal and refresh campaign data
            setShowUploadAssetsModal(false);
            resetSelectedPosts();
            toastSuccess('Assets uploaded successfully');

            // Refresh full campaign details to update error status and analytics
            await getCampaignDetails();
        } catch (error) {
            console.error('Error uploading new assets:', error);
            toastError('Failed to upload new assets. Please try again.');
        } finally {
            setIsUploadingAssets(false);
        }
    };

    /** Render the Upload Assets Modal */
    const renderUploadAssetsModal = () => {
        const modalTitle = "Upload New Assets";
        let modalSubtitle = "Add new creative assets to your campaign. You can select as many assets as you want.";

        if (usesExpressSetup) {
            modalSubtitle = "You can't edit this campaign. Run a campaign using your Facebook Login to unlock editing abilities.";
        }

        return (
            <ModalSymphony
                className="uploadAssetsModal"
                isOpen={showUploadAssetsModal}
                onClose={() => {
                    setShowUploadAssetsModal(false);
                    resetSelectedPosts();
                }}
                title={modalTitle}
                subtitle={modalSubtitle}
                buttonLeftText="Cancel"
                buttonRightText={usesExpressSetup ? "Got it" : "Add Assets"}
                buttonLeftVariant="outlined"
                buttonRightVariant="primary"
                onClickButtonLeft={() => {
                    setShowUploadAssetsModal(false);
                    resetSelectedPosts();
                }}
                onClickButtonRight={handleUploadNewAssets}
                buttonRightIsLoading={isUploadingAssets && !usesExpressSetup}
                buttonRightDisabled={isUploadingAssets}
                size="large"
            >
                {!usesExpressSetup && (
                    <AdCreativeSelectorCampaign
                        className="uploadAssetsSelector"
                        allowedUploadTypes={['posts', 'upload', 'url-search']}
                        variant="facebook"
                        hideTitle
                        hideSubtitle
                    />
                )}
            </ModalSymphony>
        );
    };

    // Add Express Setup Modal
    const [showExpressSetupModal, setShowExpressSetupModal] = useState<boolean>(false);

    const renderExpressSetupModal = () => {
        return (
            <ModalSymphony
                className="expressSetupModal"
                isOpen={showExpressSetupModal}
                onClose={() => setShowExpressSetupModal(false)}
                title="Cannot Modify Assets"
                subtitle="You can't edit assets for this campaign. Run a campaign using your Facebook Login to unlock editing abilities."
                buttonRightText="Got it"
                buttonRightVariant="primary"
                onClickButtonRight={() => setShowExpressSetupModal(false)}
                size="small"
            />
        );
    };

    // Add this modal to the render function where other modals are defined
    const renderInvalidStatusModal = () => {
        return (
            <ModalSymphony
                className="invalidStatusModal"
                isOpen={showInvalidStatusModal}
                onClose={() => setShowInvalidStatusModal(false)}
                title="Cannot Upload Assets"
                subtitle="You can only update assets for a campaign that's active and running."
                buttonRightText="Close"
                buttonRightVariant="primary"
                onClickButtonRight={() => setShowInvalidStatusModal(false)}
                size="small"
            />
        );
    };

    // NOTE: render()
    return (
        <>
            {renderCampaignDetailsHeader()}
            {/* <DebugBox
                data={{
                    isLoadingCampaignAnalytics,
                    campaignAnalyticsData,
                    campaignDetails,
                    isLoadingCampaign,
                }}
                alwaysShow
            /> */}
            {renderMainContent()}
            {renderUploadAssetsModal()}
            {renderInvalidStatusModal()}
            {renderExpressSetupModal()}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    campaignDetailsV2Component: {
        display: 'flex',
        gap: 16,
        width: '100%',
        paddingTop: 106,
    },
    campaignDetailsV2Component_mobile: {
        flexDirection: 'column',
        paddingTop: 132,
    },
    goalRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4
    },
    campaignDetailsV2Card: {
        padding: 16,
    },
    campaignDetailsV2Card_mobile: {
        padding: 8,
    },
    campaignDetailsRightSideContent: {
        position: 'sticky',
        top: 108 + 24,
        width: 280,
        minHeight: 100,
        borderRadius: 8,
        flexShrink: 0,
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    campaignDetailsRightSideContent_mobile: {
        width: '100%',
    },
    tabCampaignDetails: {
        gap: 16,
    },
    creativePerformanceHeader: {
        backgroundColor: 'white',
        padding: 16,
    },
    conversionsEnabledTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between'
    },
    conversionsEnabledTitleContainer_mobile: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    conversionsEnabledContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4
    },
    conversionsEnabledIconContainer: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 4
    },
    conversionsEnabledText: {
        ...Sty.text12,
        color: Colors.purple
    },
    goalContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: 8,
        borderRadius: 8,
        gap: 4
    },
    campaignBannerButtons: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
    },

    assetPerformanceCardBanner: {
        display: 'flex',
        flexDirection: 'row',
        border: Sty.border,
        borderRadius: 8,
        height: 200,
        overflow: 'hidden'
    },
    assetPerformanceCardBanner_mobile: {
        flexDirection: 'column',
        height: 'auto',
    },
    assetThumbnail: {
        height: '100%',
        width: 115,
        objectFit: 'cover',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        flexShrink: 0,
    },
    assetThumbnail_mobile: {
        height: 160,
        width: '100%',
        marginRight: 0,
        maxWidth: '100%',
    },
    assetPerformanceCardButtonsContainer: {
        marginTop: 0,
        alignSelf: 'auto',
        marginBottom: 8,
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
    },
    assetPerformanceCardButtonsContainer_mobile: {
        alignSelf: 'flex-start',
    },
    assetsTabContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginTop: 12
    },
    statsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 8,
    },
    assetPerformanceCardContent: {
        padding: 16,
    },
    assetPerformanceCardContent_mobile: {
        padding: 8,
    },
    setupDetailsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginTop: 12
    },
    setupDetailCard: {
        display: 'flex',
        flexDirection: 'column',
        border: Sty.border,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'white'
    },
    setupDetailCardHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        borderBottom: Sty.border,
        gap: 12
    },
    setupDetailIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: Colors.greyLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    setupDetailCardContent: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    setupDetailCardItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    assetsTabControls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
    },
    assetsTabControls_mobile: {
        flexDirection: 'column',
        gap: 16,
        alignItems: 'stretch'
    },
    assetSortSelect: {
        width: '30%',
        marginRight: 16
    },
    assetSortSelect_mobile: {
        width: '100%',
        marginRight: 0
    }
}

export default CampaignDetailsV2;